import { isPasswordAcceptable } from '@acasa/shared'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AuthError, AuthShell, PRIMARY_BUTTON } from '@/features/auth/auth-shell'
import { PasswordRules } from '@/features/auth/password-rules'
import { usePasswordResetInfo, useResetPassword } from '@/features/auth/use-auth'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'

/**
 * Redeeming a reset link: a new password, and nothing else.
 *
 * The authenticator stays exactly as it was, so whoever holds the link — which
 * may have travelled by e-mail — still has only one of the two factors. A lost
 * authenticator is a different link ("neu einladen"), which starts enrolment
 * over.
 *
 * Success does NOT sign anybody in, for the same reason: the code from the app
 * is asked at the login page like any other time. The page says so and waits
 * rather than navigating, so the sentence is actually read.
 */
export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>()
  const info = usePasswordResetInfo(token)
  const reset = useResetPassword(token)

  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')

  if (info.isLoading) {
    return (
      <AuthShell title="Link wird geprüft">
        <p className="text-muted text-sm">Einen Moment …</p>
      </AuthShell>
    )
  }

  if (reset.isSuccess) {
    return (
      <AuthShell
        title="Passwort gesetzt"
        description="Die Anmeldung funktioniert ab sofort mit dem neuen Passwort und dem Code aus der Authenticator-App."
      >
        <Link to="/anmelden" className={`${PRIMARY_BUTTON} block text-center`}>
          Zur Anmeldung
        </Link>
      </AuthShell>
    )
  }

  if (info.isError || !info.data) {
    return (
      <AuthShell
        title="Link ungültig"
        description="Der Link ist abgelaufen oder wurde bereits verwendet."
        footer="Ein Administrator kann einen neuen Link ausstellen — oder über „Passwort vergessen?“ einen neuen anfordern."
      >
        <Link
          to="/anmelden"
          className="text-muted hover:text-ink text-xs underline underline-offset-4"
        >
          Zur Anmeldung
        </Link>
      </AuthShell>
    )
  }

  const account = info.data
  const mismatch = repeat.length > 0 && password !== repeat

  return (
    <AuthShell
      title="Neues Passwort"
      description={`Für das Konto ${account.email}. Die Authenticator-App bleibt, wie sie ist — nur das Passwort wird ersetzt.`}
      footer="Der Link ist einmalig gültig."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          reset.mutate(password)
        }}
      >
        {/* A hidden username field is what lets a password manager update the
            entry for this account instead of filing a new one beside it. */}
        <input type="email" value={account.email} autoComplete="username" readOnly hidden />

        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Neues Passwort"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Passwort wiederholen"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          required
        />

        {mismatch && (
          <p className="text-danger text-xs">Die beiden Eingaben stimmen nicht überein.</p>
        )}

        <PasswordRules password={password} subject={account} />

        <button
          type="submit"
          className={PRIMARY_BUTTON}
          disabled={
            reset.isPending || password !== repeat || !isPasswordAcceptable(password, account)
          }
        >
          {reset.isPending ? 'wird gespeichert …' : 'Passwort speichern'}
        </button>

        <AuthError message={errorMessage(reset.error)} />
      </form>
    </AuthShell>
  )
}

/**
 * Narrower than the login and enrolment pages' helpers: no passkey ceremony
 * runs here, so there is no browser cancellation to translate.
 */
function errorMessage(error: unknown): string | null {
  if (!error) return null
  if (error instanceof ApiRequestError || error instanceof Error) return error.message
  return 'Unbekannter Fehler'
}
